// @ts-nocheck
/* eslint-disable react/prop-types */
import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  Typography,
} from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import React, { useCallback } from 'react';

import { EMPTY_OBJECT } from '../../const/CommonConst';
import { ReactComponent as DropdownArrow } from '../../assets/images/DropdownArrow.svg';
import { ReactComponent as DropdownArrowDisabled } from '../../assets/images/DropDownDisabled.svg';
import { isValueEmpty } from '../../utils/Utils';

/**
 * @function DropDown
 * @description common component function of dropdown
 * @param {function} onChange - on change function
 * @param {string} label - label of dropdown
 * @param {string} name - name of dropdown
 * @param {string} value - value of dropdown
 * @param {string} helperText - helper text of dropdown
 * @param {boolean} required - required of dropdown
 * @param {string} placeholder - placeholder of dropdown
 * @returns {JSX}
 */
const DropDown = React.memo(
  ({
    label,
    name,
    value,
    onChange,
    helperText,
    children,
    required = false,
    size,
    placeholder,
    labelSx,
    shrink = true,
    styles = EMPTY_OBJECT,
    selectStyles = EMPTY_OBJECT,
    disabled,
    disablePortal = true,
    ...props
  }) => {
    const [labelShrinkState, setLabelShrinkState] = React.useState();
    const inputLabel = React.useRef();
    const newPlaceholder = placeholder || label;
    let observer;
    React.useEffect(() => {
      if (inputLabel?.current) {
        // Select the node that will be observed for mutations
        // const targetNode = document.getElementById('some-id');
        // Options for the observer (which mutations to observe)
        const config = { attributes: true, childList: true, subtree: true };

        // Create an observer instance linked to the callback function
        observer = new MutationObserver((mutationList, _observer) => {
          mutationList.forEach((item) => {
            if (
              item?.type === 'attributes' &&
              item?.attributeName === 'data-shrink'
            ) {
              setLabelShrinkState(item?.target?.getAttribute('data-shrink'));
            }
          });
        });

        // Start observing the target node for configured mutations
        observer.observe(inputLabel?.current, config);
      }

      return () => {
        if (observer) {
          try {
            observer.disconnect();
          } catch (e) {
            throw new Error(e);
          }
        }
      };
    }, [inputLabel?.current]);

    const getIconComponent = useCallback(
      (_props) => {
        return props?.disabled ? (
          <DropdownArrowDisabled {..._props} />
        ) : (
          <DropdownArrow {..._props} />
        );
      },
      [props?.disabled]
    );

    const selectRenderValue = useCallback(
      () => (
        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: '400',
            opacity: '1',
            color: 'text.placeholder',
          }}
        >
          {newPlaceholder}
        </Typography>
      ),
      [newPlaceholder]
    );

    return (
      <FormControl
        error={Boolean(helperText)}
        variant="outlined"
        disabled={disabled}
        sx={{ marginTop: shrink ? '22px' : '0px', ...styles }}
        size={size}
        fullWidth
      >
        {/* If we do not want to show the label then will send shrink as false  */}
        {shrink && (
          <InputLabel
            id="nimbus-select-dropdown-label"
            required={required}
            ref={inputLabel}
            sx={{
              transform: 'none',
              top: '-24px',
              ...labelSx,
            }}
          >
            {label}
          </InputLabel>
        )}
        <Select
          labelId="nimbus-select-dropdown-label"
          disabled={disabled}
          name={name}
          displayEmpty
          value={value || ''}
          onChange={onChange}
          IconComponent={getIconComponent}
          renderValue={!isValueEmpty(value) ? undefined : selectRenderValue}
          inputProps={{
            'aria-label': label || 'Select option',
          }}
          MenuProps={{
            disablePortal: disablePortal,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'left',
            },
            style: {
              marginTop: '0.1rem',
            },
            sx: {
              '& .MuiList-root': {
                padding: '6px',
                fontSize: 14,
              },
              '& .MuiPopver-paper': {
                marginTop: '5px',
              },
              '& .MuiMenu-paper': {
                marginTop: '5px',
                maxWidth: '300px', // Limit the maximum width
                minWidth: '200px', // Set minimum width
              },
              '& .MuiMenuItem-root': {
                marginTop: '4px',
                height: '40px',
                '&:hover': {},
                '&:active': {
                  '&&&:before': {},
                  '&&:after': {},
                },
                '&:focus': {},
              },
            },
            PaperProps: {
              sx: {
                maxHeight: 'calc(100% - 60vh)',
                maxWidth: '300px', // Limit the maximum width of the dropdown
                minWidth: '200px', // Set minimum width
              },
            },
            // getContentAnchorEl: null,
          }}
          {...props}
          variant="outlined"
          sx={{
            '& .MuiFilledInput-input': {
              lineHeight: '19.07px',
              fontWeight: '500 !important',
              fontStyle: 'normal',
            },

            '& .MuiSelect-select .notranslate::after':
              newPlaceholder && labelShrinkState === 'true'
                ? {
                    content: `"${newPlaceholder}"`,
                    opacity: 0.42,
                  }
                : {},
            ...selectStyles,
            ...props?.sx,
          }}
        >
          {children}
        </Select>
        <FormHelperText>{helperText}</FormHelperText>
      </FormControl>
    );
  }
);
const menuItemSx = () => ({
  '&.Mui-disabled': {},
});
const DropdownItem = React.memo(({ children, ...props }) => (
  <MenuItem {...props} sx={menuItemSx}>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
      }}
    >
      <Typography
        variant="body2"
        sx={{
          flexGrow: 1,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {children}
      </Typography>

      {props?.metaData && (
        <Typography
          variant="body5"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            color: 'text.secondary',
            marginLeft: 1,
            whiteSpace: 'nowrap',
          }}
        >
          {props?.metaData}
        </Typography>
      )}
    </Box>
  </MenuItem>
));

export { DropDown, DropdownItem };
